import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import { UserModel } from "../../models/user.model";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useAuthenticatedFetch } from "../../utils/auth-fetch";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { CopyIcon, RefreshCwIcon, Trash2Icon } from "lucide-react";
import { UserContext } from "../../contexts/UserContext";
import { UserRole } from "../../models/user-role.model";
import ConfirmationDialog from "../../confirmation-dialog/confirmation-dialog";

export interface DialogProps {
  open: boolean;
  selectedValue: string;
  onClose: (value: string) => void;
}

const InviteUser = ({ open, onClose, userForResetPassword }: { open: boolean; onClose: () => void; userForResetPassword?: UserModel }) => {
  const [formData, setFormData] = useState({
    email: userForResetPassword?.email ?? "",
    password: "",
  });

  useEffect(() => {
    if (open && !userForResetPassword) {
      setFormData({
        email: "",
        password: crypto.getRandomValues(new Uint32Array(1))[0].toString(16) + crypto.getRandomValues(new Uint32Array(1))[0].toString(16),
      });
    } else if (open && userForResetPassword) {
      setFormData({
        email: userForResetPassword.email,
        password: crypto.getRandomValues(new Uint32Array(1))[0].toString(16) + crypto.getRandomValues(new Uint32Array(1))[0].toString(16),
      });
    }
  }, [open]);

  const authenticatedFetch = useAuthenticatedFetch();
  const { showSnackbar } = useSnackbar();

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleInvite = async () => {
    await authenticatedFetch("/api/user/invite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    }).then((res) => {
      if (res.ok) {
        showSnackbar("User invited successfully", "success");
        onClose();
      } else {
        showSnackbar("Failed to invite user", "error");
      }
    });
  };

  const handleReset = async () => {
    if(!userForResetPassword) return;
    await authenticatedFetch(`/api/user/update/password/${userForResetPassword._id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    }).then((res) => {
      if (res.ok) {
        showSnackbar("User updated successfully", "success");
        onClose();
      } else {
        showSnackbar("Failed to update user", "error");
      }
    });
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if(!userForResetPassword) await handleInvite();
    else await handleReset();
  };

  const handleCopy = () => {
    setTimeout(() => {
      navigator.clipboard.writeText(formData.password)
    }, 0)
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{userForResetPassword ? "Reset" : "Invite"} User</DialogTitle>
      <form onSubmit={handleSubmit} className="pw-4">
        <DialogContent>
          <TextField label="Email Address" type="email" disabled={userForResetPassword !== undefined} name="email" value={formData.email} onChange={handleChange} required fullWidth margin="normal" />

          <TextField
            label="Password"
            type="text"
            name="password"
            value={formData.password}
            required
            fullWidth
            margin="normal"
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <Tooltip title="Copy">
                    <IconButton onClick={handleCopy}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                ),
              },
            }}
          />
          <Typography variant="caption" className="flex justify-center w-full text-center">
            Don't forget to save the password.
          </Typography>
        </DialogContent>
        <DialogActions>
          <button type="button" className="w-36 text-orange-600 py-2 m-2 px-4 rounded float-end" onClick={onClose}>
            Close
          </button>
          <button type="submit" className="w-36 bg-orange-600 text-white py-2 m-2 px-4 rounded hover:bg-orange-700 float-end">
            {userForResetPassword ? "Reset" : "Invite"}
          </button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

function Users() {
  const [usersData, setUsersData] = useState<UserModel[]>([]);
  const { currentUser } = useContext(UserContext);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [confirmDeleteUserDialogOpen, setConfirmDeleteUserDialogOpen] = useState(false);
  const [userIdForDeletion, setUserIdForDeletion] = useState<string>("");
  const [userForResetPassword, setUserForResetPassword] = useState<UserModel>();

  const { showSnackbar } = useSnackbar();
  const authenticatedFetch = useAuthenticatedFetch();

  useEffect(() => {
    getUsers();
  }, []);

  const handleDialogOpen = () => {
    setInviteDialogOpen(true);
  };

  const handleDialogClose = () => {
    setInviteDialogOpen(false);
    setUserForResetPassword(undefined);
    getUsers();
  };

  const handleConfirm = async (confirmed: boolean) => {
    if (userIdForDeletion === "") {
      return;
    }
    if (confirmed) {
      await handleUserDelete(userIdForDeletion);
      setUserIdForDeletion("");
    }
  };

  const handleRoleChange = async (userId: string, event: ChangeEvent<HTMLSelectElement>) => {
    await authenticatedFetch(`/api/user/update/role/${userId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role: event.target.value as UserRole }),
    }).then((res) => {
      if (res.ok) {
        showSnackbar("User role updated successfully", "success");
        getUsers();
      } else {
        showSnackbar("Failed to update user role", "error");
      }
    });
  };

  const handleResetPassword = async (user: UserModel) => {
    setUserForResetPassword(user);
    setInviteDialogOpen(true);
  };

  const handleUserDelete = async (userId: string) => {
    await authenticatedFetch(`/api/user/${userId}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    }).then((res) => {
      if (res.ok) {
        showSnackbar("User deleted successfully", "success");
        getUsers();
      } else {
        showSnackbar("Failed to delete user", "error");
      }
    });
  };

  const getUsers = async () => {
    authenticatedFetch("/api/user/")
      .then((res) => res.json())
      .then((data) => {
        setUsersData(data["message"] === undefined ? data : []);
      })
      .catch((error) => console.error("Error fetching user:", error));
  };

  return !currentUser ? null : (
    <div className="overflow-x-auto w-full">
      <button type="button" className="w-36 bg-orange-600 text-white py-2 m-2 px-4 rounded hover:bg-orange-700 float-end" onClick={handleDialogOpen}>
        + Invite
      </button>
      <InviteUser open={inviteDialogOpen} onClose={handleDialogClose} userForResetPassword={userForResetPassword} />
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              E-Mail
            </th>
            <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Role
            </th>
            <th scope="col" className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {usersData.map((user) => (
            <tr key={user._id}>
              <td className="px-6 py-4 whitespace-nowrap">{user.email}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <select className="w-full p-2 border rounded" disabled={user._id === (currentUser as any).id} value={user.role} onChange={(event) => handleRoleChange(user._id, event)}>
                  <option value="SYSADMIN">SYSADMIN</option>
                  <option value="MAINTAINER">MAINTAINER</option>
                  <option value="ANALYST">ANALYST</option>
                </select>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <Tooltip title="Reset Password">
                  <IconButton aria-label="reset password" onClick={() => handleResetPassword(user)}>
                    <RefreshCwIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    aria-label="delete"
                    onClick={() => {
                      setConfirmDeleteUserDialogOpen(true);
                      setUserIdForDeletion(user._id);
                    }}
                    disabled={user._id === (currentUser as any).id}
                  >
                    <Trash2Icon />
                  </IconButton>
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ConfirmationDialog
        open={confirmDeleteUserDialogOpen}
        onClose={() => {
          setConfirmDeleteUserDialogOpen(false);
          setUserIdForDeletion("");
        }}
        title="Are you sure?"
        description="This action cannot be undone."
        onConfirm={(event: boolean) => handleConfirm(event)}
      />
    </div>
  );
}

export default Users;
